/**
 * Augustine CMS — shared site config types and default config.
 *
 * The public site fetches a live `/site-config.json` from the same site origin
 * at runtime.
 * This file provides:
 *   1. TypeScript types for the config shape
 *   2. A `defaultConfig` constant used as fallback when the CDN is unavailable
 *   3. A `normalizeSiteConfig()` helper that deep-merges live config onto the
 *      defaults so newly added CMS fields remain safe on older stored config
 */

import { migrateLegacyCategory } from "./galleryCategory";

export interface GalleryItem {
  id: string;
  url: string;
  /** Optional small, pre-resampled copy of `url` used for the grid and
   *  sidebar carousel thumbnails. Generated client-side at upload time (or
   *  backfilled from the admin). Avoids the browser downscaling the full-res
   *  original into a tiny tile, which aliases high-frequency detail (e.g.
   *  roof shingles) into a moiré shimmer. The lightbox still uses `url`.
   *  Falls back to `url` when absent, so older configs render unchanged. */
  thumbnailUrl?: string;
  caption: string;
  category: string;
  active: boolean;
  order: number;
  /** Marks a photo as a primary/featured shot for its service. Only primary
   *  photos appear in the service-page sidebar carousel; the full gallery
   *  grid still shows every active photo. */
  primary: boolean;
}

export interface HomepageStat {
  value: string;
  label: string;
}

export interface HomepageServiceCard {
  title: string;
  description: string;
  /** Internal route the card links to. Set by code, not the CMS — admins
   *  can't edit this from the dashboard so they can't accidentally point
   *  a card at a 404. The Homepage tab edits everything else on the card. */
  href: string;
  /** Key into serviceCardIconRegistry. See lib/serviceCardIcons.ts.
   *  Falls back to a Hammer icon if the key isn't recognized. */
  icon: string;
}

export interface HomepageWhyChooseUsItem {
  title: string;
  description: string;
}

export interface HomepageFeaturedReviews {
  heading: string;
  subheading: string;
}

export interface HomepageCtaBlock {
  title: string;
  body: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryHref: string;
  footnote: string;
}

export interface CompanyHours {
  weekdays: string;
  saturday: string;
  sunday: string;
}

export interface CompanyValueItem {
  title: string;
  description: string;
}

export interface ReviewItem {
  name: string;
  location: string;
  service: string;
  text: string;
  rating: number;
  featured: boolean;
  /** Service-page slugs this review should appear on. Empty/omitted =
   *  not pinned to any specific service page. Independent of the free-text
   *  `service` label, which is just for display under the reviewer name. */
  serviceSlugs?: string[];
}

export interface ServiceAreaTown {
  name: string;
  slug: string;
}

export interface ServiceAreaCounty {
  name: string;
  state: string;
  towns: ServiceAreaTown[];
}

/**
 * A single trust badge: text + an icon key from
 * `lib/trustBadgeIcons.ts`. Used by the footer trust column
 * (`brand.trustBadges`), the homepage hero trust row (`hero.badges`),
 * and the contact page's "Why Choose Augustine?" panel
 * (`contactPage.trustItems`).
 *
 * The icon is a stable kebab-case key into the trust-badge icon registry
 * (e.g. "shield", "star", "check-circle"). Storing the key — not the
 * component reference — means adding/renaming icons in the registry
 * doesn't have to touch saved configs in S3.
 *
 * Renderers should filter rows where `text` is empty so admins can
 * blank a badge without leaving an orphan icon in the UI.
 */
export interface TrustBadge {
  text: string;
  icon: string;
}

/**
 * A single social platform link shown in the footer.
 *
 * `platform` is a stable kebab-case key into `lib/socialPlatformIcons.ts`
 * (e.g. "facebook", "instagram") — drives both the icon and the default
 * link label. Renderers filter out rows with an empty `url` so admins
 * can blank a link to remove it.
 *
 * `label` is the visible link text. Empty falls back to the platform's
 * default label (e.g. "Follow on Facebook").
 */
export interface SocialLink {
  platform: string;
  url: string;
  label: string;
}

/**
 * Per-service-page editable copy. Keyed by service slug (matches the route
 * folder under `app/(site)/<slug>/` and `KNOWN_SERVICE_SLUGS` in
 * lib/serviceSlugs.ts).
 *
 * The H1 + `subtitle` render inside the green hero box at the top of each
 * service detail page. `description` is the body paragraph immediately below.
 * `highlights` is the "What's Included" bullet list.
 */
export interface ServiceDetail {
  /** The page's H1, written around the phrase people search for. Empty or
   *  missing falls back to `${title} in Chester County, PA`. */
  headline?: string;
  /** The service's name, used in the default H1 and in section headings. */
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
}

export interface SiteConfig {
  _version: number;
  _updatedAt: string;
  /** Draft only: the editor's email. Never in the public live config. */
  _updatedBy?: string;

  brand: {
    logoUrl: string;
    /** Optional alternate logo shown when the navbar has a solid (white)
     *  background — i.e. on scroll or non-homepage routes. Designed for
     *  light backgrounds (dark letters). When empty, the navbar falls back
     *  to `logoUrl` wrapped in a white pill so a light-letter primary logo
     *  stays readable over the hero. */
    logoDarkUrl: string;
    logoAlt: string;
    faviconUrl: string;
    /** Short paragraph shown in the footer brand column.
     *  Lived under `company` in v1 schema; migrated by normalizeSiteConfig. */
    footerBlurb: string;
    /** Trust badges shown in the footer brand column. Empty `text`
     *  rows are filtered out by the renderer so admins can clear a
     *  badge without leaving an orphan icon. Lived under `company`
     *  as `string[]` in v1 schema; migrated by normalizeSiteConfig. */
    trustBadges: TrustBadge[];
  };

  hero: {
    imageUrl: string;
    videoUrl: string;
    /** Override the auto-detected video aspect ratio. Empty string
     *  means "use the file's intrinsic ratio" (auto-detect). Otherwise
     *  one of the preset slash-separated values surfaced in the CMS
     *  dropdown — e.g. "16/9", "1/1", "9/16", "4/5". */
    videoAspectRatio: string;
    headline: string;
    subheadline: string;
    /** Trust badges shown beneath the hero CTA. Same shape and
     *  empty-row filtering as `brand.trustBadges`. */
    badges: TrustBadge[];
  };

  homepage: {
    stats: HomepageStat[];
    servicesHeading: string;
    servicesSubheading: string;
    services: HomepageServiceCard[];
    whyChooseUsHeading: string;
    whyChooseUsItems: HomepageWhyChooseUsItem[];
    featuredReviews: HomepageFeaturedReviews;
    miniCta: HomepageCtaBlock;
    bottomCta: HomepageCtaBlock;
  };

  company: {
    aboutHeading: string;
    aboutSubheading: string;
    aboutBody: string[];
    valuesHeading: string;
    values: CompanyValueItem[];
    hours: CompanyHours;
  };

  reviews: {
    heading: string;
    subheading: string;
    averageLabel: string;
    ctaTitle: string;
    ctaBody: string;
    ctaLabel: string;
    items: ReviewItem[];
  };

  gallery: GalleryItem[];

  contact: {
    phone: string;
    email: string;
    /** Pennsylvania Home Improvement Contractor registration number. PA law
     *  requires it in all advertising, so the footer shows it on every page.
     *  Hidden when blank. */
    hicNumber: string;
    serviceArea: string;
    /** Social platform links shown in the footer. Add/remove rows from
     *  the Contact tab. Empty `url` rows are hidden at render time. */
    socialLinks: SocialLink[];
    /** Label for the "Get a Free Estimate" button shown at the bottom
     *  of the footer Contact column. */
    footerCtaLabel: string;
    /** Internal href for the footer estimate button. */
    footerCtaHref: string;
  };

  features: {
    showVeteranBadge: boolean;
    showTrexProBadge: boolean;
    heroEnabled: boolean;
  };

  serviceAreas: ServiceAreaCounty[];

  /** Per-service-page editable copy, keyed by slug. */
  serviceDetails: Record<string, ServiceDetail>;

  /** Page copy for `/contact-us`. Headings, intros, sidebar labels,
   *  form microcopy, and the success-message text. */
  contactPage: {
    heading: string;
    intro: string;
    sidebarHeading: string;
    trustHeading: string;
    /** Trust indicators shown in the contact page's right-rail panel.
     *  Same shape as `brand.trustBadges`. */
    trustItems: TrustBadge[];
    formIntroLabel: string;
    formMicrocopy: string;
    submitLabel: string;
    submitSendingLabel: string;
    successHeading: string;
    successBody: string;
    phoneLabel: string;
    emailLabel: string;
    hoursLabel: string;
    serviceAreaLabel: string;
  };

  /** Page copy for `/gallery`. */
  galleryPage: {
    tagline: string;
    heading: string;
    intro: string;
    ctaTagline: string;
    ctaHeading: string;
    ctaBody: string;
    ctaButtonLabel: string;
  };

  /** Shared copy used by all six service detail pages.
   *  `galleryHeading` and `galleryIntro` accept a `{service}` placeholder
   *  that gets replaced with the page's service title at render time. */
  servicePage: {
    highlightsHeading: string;
    sidebarTitle: string;
    sidebarBody: string;
    sidebarPrimaryLabel: string;
    sidebarDisclaimer: string;
    galleryHeading: string;
    galleryIntro: string;
  };
}

export const defaultConfig: SiteConfig = {
  _version: 1,
  _updatedAt: "2026-03-31T00:00:00Z",

  brand: {
    logoUrl: "",
    logoDarkUrl: "",
    logoAlt: "Augustine Home Improvements",
    faviconUrl: "",
    footerBlurb:
      "Veteran-owned home improvement contractor serving Chester County, PA and suburban Philadelphia since 2020.",
    trustBadges: [
      { text: "Licensed & Insured", icon: "shield" },
      { text: "Veteran-Owned & Operated", icon: "award" },
    ],
  },

  hero: {
    imageUrl: "",
    videoUrl: "",
    videoAspectRatio: "",
    headline: "Expert Home Improvements",
    subheadline:
      "Serving Chester County PA and suburban Philadelphia. Decks, kitchens, bathrooms, basements, additions: one contractor you can trust from start to finish.",
    badges: [
      { text: "Licensed & Insured", icon: "shield" },
      { text: "5-Star Rated", icon: "star" },
      { text: "Free Estimates", icon: "check-circle" },
    ],
  },

  homepage: {
    stats: [
      { value: "100+", label: "Projects Completed" },
      { value: "5★", label: "Average Rating" },
      { value: "4+", label: "Years Experience" },
      { value: "Free", label: "Estimates" },
    ],
    servicesHeading: "Our Services",
    servicesSubheading:
      "From a new deck to a full home renovation, we handle projects of every size with the same attention to detail.",
    services: [
      {
        title: "Deck Installation",
        description:
          "Custom composite and wood decks designed for beauty and lasting durability.",
        href: "/deck-installation/",
        icon: "layers",
      },
      {
        title: "Kitchen Renovations",
        description:
          "Full kitchen remodels from layout redesign to the finishing touches: cabinets, counters, and everything in between.",
        href: "/kitchen-renovations/",
        icon: "chef-hat",
      },
      {
        title: "Bathroom Remodeling",
        description:
          "Transform your bathroom into a personal retreat. Custom tile, fixtures, walk-in showers, and complete gut renovations.",
        href: "/bathroom-remodeling/",
        icon: "bath",
      },
      {
        title: "Basement Renovation",
        description:
          "Turn your unfinished basement into livable space: home offices, rec rooms, gyms, and in-law suites.",
        href: "/basement-renovation/",
        icon: "arrow-down-to-line",
      },
      {
        title: "Home Additions",
        description:
          "Need more space? We build seamless additions that match your home's architecture and expand your living area.",
        href: "/home-additions/",
        icon: "home",
      },
      {
        title: "Home Renovations",
        description:
          "Whole-home transformations and multi-room remodels. One contractor, one point of contact, start to finish.",
        href: "/home-renovations/",
        icon: "hammer",
      },
    ],
    whyChooseUsHeading: "Why Chester County Homeowners Choose Augustine",
    whyChooseUsItems: [
      {
        title: "Veteran-Owned & Operated",
        description:
          "Founded by Brandon Augustine, a US veteran who brings discipline, attention to detail, and integrity to every project.",
      },
      {
        title: "Licensed & Fully Insured",
        description:
          "Your home is protected. We carry full liability coverage and workers' comp on every job.",
      },
      {
        title: "Local, Not a Franchise",
        description:
          "We live and work in this community. Our reputation here matters, and it shows in the care we take.",
      },
    ],
    featuredReviews: {
      heading: "What Our Customers Say",
      subheading: "5.0 average · 3+ verified reviews",
    },
    miniCta: {
      title: "Ready to Start Your Project?",
      body: "Get a free, no-obligation estimate. We typically respond within one business day.",
      primaryLabel: "Request a Free Estimate",
      primaryHref: "/contact-us/",
      secondaryLabel: "Call 484-467-7925",
      secondaryHref: "tel:+14844677925",
      footnote: "Mon–Fri 8am–6pm · Sat 9am–3pm",
    },
    bottomCta: {
      title: "Let&apos;s Build Something Together",
      body: "Free estimates, honest pricing, quality craftsmanship. Serving Chester County PA and surrounding areas.",
      primaryLabel: "Get a Free Estimate",
      primaryHref: "/contact-us/",
      secondaryLabel: "484-467-7925",
      secondaryHref: "tel:+14844677925",
      footnote: "",
    },
  },

  company: {
    aboutHeading: "About Augustine Home Improvements",
    aboutSubheading:
      "A veteran-owned and operated home improvement company serving Chester County PA and suburban Philadelphia since 2020.",
    aboutBody: [
      "Augustine Home Improvements was founded by Brandon Augustine, a United States veteran who brings the same discipline, attention to detail, and commitment to excellence from his military service to every home improvement project he undertakes.",
      "Based in Chester County, Pennsylvania, we specialize in decks, kitchen renovations, bathroom remodeling, basement finishing, home additions, and full-home renovations. We serve homeowners throughout Chester County and the surrounding Philadelphia suburbs.",
      "We&apos;re fully licensed and insured, and we&apos;re committed to honest pricing, clear communication, and work that stands the test of time. When you hire Augustine Home Improvements, you get Brandon, not a salesman who hands you off to a crew you&apos;ve never met.",
    ],
    valuesHeading: "Our Values",
    values: [
      {
        title: "Integrity First",
        description:
          "Honest estimates, no hidden fees, clear scope of work before we start.",
      },
      {
        title: "Quality Craftsmanship",
        description:
          "We take pride in every detail, from framing to finish work.",
      },
      {
        title: "Veteran Standard",
        description:
          "Military discipline means showing up on time, staying on schedule, and getting it right.",
      },
      {
        title: "Community Focus",
        description:
          "We live and work in Chester County. Our neighbors are our customers.",
      },
    ],
    hours: {
      weekdays: "Mon–Fri: 8:00am – 6:00pm",
      saturday: "Saturday: 9:00am – 3:00pm",
      sunday: "Sunday: Closed",
    },
  },

  reviews: {
    heading: "Customer Reviews",
    subheading:
      "What Chester County homeowners say about working with Augustine Home Improvements.",
    averageLabel: "5.0 average",
    ctaTitle: "Ready to Be Our Next Happy Customer?",
    ctaBody: "Get a free estimate today. No obligation, no pressure.",
    ctaLabel: "Request a Free Estimate",
    items: [
      {
        name: "Sarah M.",
        location: "Phoenixville, PA",
        service: "Deck Installation",
        text: "Brandon and his crew did an amazing job on our deck. Professional, clean, and finished ahead of schedule. The composite decking looks beautiful and we couldn't be happier. Highly recommend!",
        rating: 5,
        featured: true,
        serviceSlugs: ["deck-installation"],
      },
      {
        name: "Tom K.",
        location: "Malvern, PA",
        service: "Kitchen Renovation",
        text: "We had our kitchen completely renovated. The quality of work is outstanding: new cabinets, countertops, and backsplash. Brandon was great to work with, very communicative and professional.",
        rating: 5,
        featured: true,
        serviceSlugs: ["kitchen-renovations"],
      },
      {
        name: "Jennifer L.",
        location: "Downingtown, PA",
        service: "Basement Renovation",
        text: "Augustine Home Improvements transformed our unfinished basement into a beautiful family room. Fair pricing, great communication throughout, and excellent craftsmanship. We love it!",
        rating: 5,
        featured: true,
        serviceSlugs: ["basement-renovation"],
      },
    ],
  },

  gallery: [],

  contact: {
    phone: "484-467-7925",
    email: "info@augustinehomeimprovements.com",
    hicNumber: "PA154882",
    serviceArea:
      "Chester County, PA and surrounding communities including Phoenixville, Malvern, Downingtown, West Chester, and greater Philadelphia suburbs.",
    // Seed with the existing hardcoded Facebook link so the footer
    // doesn't lose it on first load after this PR ships. Empty label
    // falls back to the platform's default ("Follow on Facebook").
    socialLinks: [
      {
        platform: "facebook",
        url: "https://www.facebook.com/augustinehomeimprovements",
        label: "",
      },
    ],
    footerCtaLabel: "Get a Free Estimate",
    footerCtaHref: "/contact-us/",
  },

  features: {
    showVeteranBadge: true,
    showTrexProBadge: false,
    heroEnabled: true,
  },

  serviceAreas: [],

  serviceDetails: {
    "deck-installation": {
      headline: "Deck Builder in Chester County, PA",
      title: "Deck Installation",
      subtitle: "Custom deck builder serving Chester County PA.",
      description:
        "Whether you're envisioning a simple pressure-treated deck or a multi-level composite outdoor living space, Augustine Home Improvements designs and builds decks that complement your home and hold up for decades.",
      highlights: [
        "Composite decking",
        "Pressure-treated and hardwood options",
        "Custom railings, stairs, and built-in benches",
        "Multi-level and wraparound deck designs",
        "Pergolas and shade structures",
        "Deck replacement and board-by-board repair",
        "Permit acquisition and code-compliant construction",
        "Cleanup and haul-away of old deck materials",
      ],
    },
    "kitchen-renovations": {
      headline: "Kitchen Remodeling Contractor in Chester County, PA",
      title: "Kitchen Renovations",
      subtitle:
        "Full kitchen remodels in Chester County PA, from layout to the last cabinet pull.",
      description:
        "The kitchen is the heart of your home, and Augustine Home Improvements treats every kitchen project with the attention it deserves. From a simple cabinet refresh to a full gut renovation that reconfigures the entire layout, we manage every phase of the project (design, demo, rough work, and all finishing) so you have a single point of contact from start to finish.",
      highlights: [
        "Full kitchen gut renovations and layout redesigns",
        "Custom and semi-custom cabinetry installation",
        "Countertop installation (quartz, granite, laminate)",
        "Tile backsplash and flooring",
        "Appliance installation and coordination",
        "Plumbing and electrical rough-in coordination",
        "Under-cabinet lighting",
        "Permit acquisition and code-compliant work",
      ],
    },
    "bathroom-remodeling": {
      headline: "Bathroom Remodeling Contractor in Chester County, PA",
      title: "Bathroom Remodeling",
      subtitle:
        "Transform your bathroom in Chester County PA, from cosmetic updates to full gut renovations.",
      description:
        "A well-designed bathroom makes every morning better. Augustine Home Improvements handles bathroom remodels of all scopes, from updating fixtures and tile to completely reconfiguring the layout of a master bath. We work with you to design a space that's both functional and beautiful, and we handle all the trades so you don't have to coordinate a dozen different contractors.",
      highlights: [
        "Complete gut renovations and layout changes",
        "Custom tile showers and tub surrounds",
        "Walk-in shower design and installation",
        "Vanity, sink, and fixture installation",
        "Heated floor installation",
        "Bathtub-to-shower conversions",
        "Accessible bathroom modifications",
        "Plumbing and electrical rough-in coordination",
      ],
    },
    "basement-renovation": {
      headline: "Basement Finishing & Renovation in Chester County, PA",
      title: "Basement Renovation",
      subtitle: "Turn your unfinished basement into livable, usable space.",
      description:
        "Your unfinished basement represents hundreds of square feet of untapped potential. Augustine Home Improvements specializes in transforming raw basements into polished living spaces: home offices, media rooms, gyms, guest suites, playrooms, and more. We manage framing, insulation, drywall, flooring, and all finishing work so you get a finished basement that feels like a natural extension of your home.",
      highlights: [
        "Full basement finishing from framing to paint",
        "Home office and remote work spaces",
        "Media rooms and home theaters",
        "Home gyms and fitness spaces",
        "In-law suites and guest rooms",
        "Egress window installation",
        "Waterproofing coordination",
        "Bar and entertainment area builds",
      ],
    },
    "home-additions": {
      headline: "Home Addition Contractor in Chester County, PA",
      title: "Home Additions",
      subtitle:
        "Expand your living space with a seamless addition that looks like it was always there.",
      description:
        "When you love your neighborhood but need more space, a home addition is often the smartest investment you can make. Augustine Home Improvements builds additions that integrate seamlessly with your existing home's architecture, materials, and style. We handle everything from foundation work to final trim, and we make sure the addition is code-compliant and properly permitted every step of the way.",
      highlights: [
        "Room additions and bump-outs",
        "Sunrooms and four-season additions",
        "Garage conversions",
        "Second-story additions",
        "Master suite additions",
        "In-law suite additions",
        "Foundation, framing, and structural work",
        "Full interior finishing to match existing home",
      ],
    },
    "home-renovations": {
      headline: "Home Renovation Contractor in Chester County, PA",
      title: "Home Renovations",
      subtitle:
        "Whole-home transformations and multi-room remodels. One point of contact, start to finish.",
      description:
        "Sometimes a single-room project turns into something bigger, or you know from the start that you want to transform your entire home. Augustine Home Improvements manages large-scale renovation projects with the same discipline and attention to detail as smaller jobs. We coordinate all trades, keep to schedule, and give you one contractor to call throughout the entire process.",
      highlights: [
        "Whole-home renovation planning and management",
        "Multi-room remodels and phased projects",
        "Historic home renovations and updates",
        "Interior reconfiguration and layout changes",
        "New flooring throughout",
        "Interior painting and trim work",
        "Door and window replacement",
        "Coordination with plumbing, electrical, and HVAC trades",
      ],
    },
  },

  contactPage: {
    heading: "Get a Free Estimate",
    intro:
      "Fill out the form below and a Augustine Home Improvements representative will contact you within the next business day. If you need immediate assistance, please call us.",
    sidebarHeading: "Contact Information",
    trustHeading: "Why Choose Augustine?",
    trustItems: [
      { text: "Licensed & Insured", icon: "shield" },
      { text: "Veteran-Owned & Operated", icon: "award" },
    ],
    formIntroLabel: "Services You Are Interested In",
    formMicrocopy:
      "* Indicates required fields. We typically respond within the next business day.",
    submitLabel: "Submit Request",
    submitSendingLabel: "Sending...",
    successHeading: "Message Received!",
    successBody:
      "Thanks for reaching out. A member of our team will contact you within the next business day. If you need immediate assistance, please call",
    phoneLabel: "Phone",
    emailLabel: "Email",
    hoursLabel: "Hours",
    serviceAreaLabel: "Service Area",
  },

  galleryPage: {
    tagline: "Crafted spaces. Lasting quality.",
    heading: "See the craftsmanship behind Augustine Home Improvements.",
    intro:
      "Browse a curated look at the decks, kitchens, bathrooms, basements, and full-home renovations that reflect Augustine's standard of care, detail, and clean execution.",
    ctaTagline: "Ready for your own transformation?",
    ctaHeading: "Let's talk about what you want to improve next.",
    ctaBody:
      "From decks and additions to kitchens and baths, Augustine Home Improvements delivers thoughtful design, durable workmanship, and a smooth remodeling experience from start to finish.",
    ctaButtonLabel: "Start Your Project",
  },

  servicePage: {
    highlightsHeading: "What's Included",
    sidebarTitle: "Get a Free Estimate",
    sidebarBody:
      "Tell us about your project and we'll get back to you within one business day.",
    sidebarPrimaryLabel: "Request Estimate",
    sidebarDisclaimer: "No obligation · Free quotes · Licensed & insured",
    galleryHeading: "Recent {service} Projects",
    galleryIntro:
      "A few completed {service} jobs from homeowners in Chester County and the surrounding suburbs.",
  },
};

/**
 * Coerce a legacy or current trust-badge value into the canonical
 * `TrustBadge[]` shape:
 *   - `string[]`  → used positional defaults (shield/star/award) for the
 *                   first three entries; "shield" for any beyond that.
 *   - `TrustBadge[]` → passed through, with missing/non-string fields
 *                      filled with safe defaults.
 *   - anything else → returns the provided fallback (a copy).
 *
 * Empty rows are preserved here (filtering happens at render time) so an
 * admin who clears a row mid-edit doesn't lose their place.
 */
function normalizeTrustBadges(raw: unknown, fallback: TrustBadge[]): TrustBadge[] {
  if (!Array.isArray(raw)) return fallback.map((b) => ({ ...b }));
  if (raw.length === 0) return fallback.map((b) => ({ ...b }));

  // Positional icon defaults preserve the legacy footer order
  // (Shield / Star / Award) when migrating string[] → TrustBadge[].
  const positionalIcons = ["shield", "star", "award"];

  return raw.map((entry, i): TrustBadge => {
    if (typeof entry === "string") {
      return {
        text: entry,
        icon: positionalIcons[i] ?? "shield",
      };
    }
    if (entry && typeof entry === "object") {
      const obj = entry as { text?: unknown; icon?: unknown };
      return {
        text: typeof obj.text === "string" ? obj.text : "",
        icon: typeof obj.icon === "string" && obj.icon ? obj.icon : "shield",
      };
    }
    return { text: "", icon: "shield" };
  });
}

/**
 * Allow only safe protocols for URLs that the renderer feeds into
 * `<a href>`, `<img src>`, or `<video src>`. Returns the original
 * string for `https?:`, `mailto:`, `tel:`, or strings that start with
 * `/`, `#`, or `?`; returns `""` for anything else (including
 * `javascript:`, `data:`, `vbscript:`, or unrecognized strings).
 *
 * Defense-in-depth for the admin-vs-public threat model: a compromised
 * admin token can write arbitrary JSON to the CMS config, but the public
 * site won't render that JSON's URLs into executable contexts.
 */
function safeUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("?")
  ) {
    return trimmed;
  }
  if (/^(https?|mailto|tel):/i.test(trimmed)) return trimmed;
  return "";
}

export function normalizeSiteConfig(config: Partial<SiteConfig> | null | undefined): SiteConfig {
  const incoming = config ?? {};

  // Trust badges + footer blurb moved from `company` → `brand` in this
  // schema rev. Read the legacy location lazily so old saves migrate
  // cleanly. New saves overwrite `brand.*` only and the legacy fields
  // fade out on the next save.
  const legacyCompany = (incoming as {
    company?: { footerBlurb?: unknown; trustBadges?: unknown };
  }).company ?? {};

  return {
    ...defaultConfig,
    ...incoming,
    brand: {
      ...defaultConfig.brand,
      ...(incoming.brand ?? {}),
      logoUrl: safeUrl(incoming.brand?.logoUrl),
      logoDarkUrl: safeUrl(incoming.brand?.logoDarkUrl),
      faviconUrl: safeUrl(incoming.brand?.faviconUrl),
      footerBlurb:
        (typeof incoming.brand?.footerBlurb === "string" && incoming.brand.footerBlurb) ||
        (typeof legacyCompany.footerBlurb === "string" ? legacyCompany.footerBlurb : "") ||
        defaultConfig.brand.footerBlurb,
      trustBadges: normalizeTrustBadges(
        incoming.brand?.trustBadges ?? legacyCompany.trustBadges,
        defaultConfig.brand.trustBadges,
      ),
    },
    hero: {
      ...defaultConfig.hero,
      ...(incoming.hero ?? {}),
      imageUrl: safeUrl(incoming.hero?.imageUrl),
      videoUrl: safeUrl(incoming.hero?.videoUrl),
      badges: normalizeTrustBadges(incoming.hero?.badges, defaultConfig.hero.badges),
    },
    homepage: {
      ...defaultConfig.homepage,
      ...(incoming.homepage ?? {}),
      stats: incoming.homepage?.stats?.length
        ? incoming.homepage.stats.map((item) => ({ ...item }))
        : defaultConfig.homepage.stats.map((item) => ({ ...item })),
      // Per-field fallback so configs saved before `icon` existed still
      // pick up the positional default icon (kept stable in defaultConfig).
      // The href is also defaulted from position so old data without href
      // doesn't render a broken link.
      services: incoming.homepage?.services?.length
        ? incoming.homepage.services.map((item, i) => ({
            title: item.title ?? defaultConfig.homepage.services[i]?.title ?? "",
            description:
              item.description ??
              defaultConfig.homepage.services[i]?.description ??
              "",
            href: item.href ?? defaultConfig.homepage.services[i]?.href ?? "",
            icon:
              item.icon ?? defaultConfig.homepage.services[i]?.icon ?? "hammer",
          }))
        : defaultConfig.homepage.services.map((item) => ({ ...item })),
      whyChooseUsItems: incoming.homepage?.whyChooseUsItems?.length
        ? incoming.homepage.whyChooseUsItems.map((item) => ({ ...item }))
        : defaultConfig.homepage.whyChooseUsItems.map((item) => ({ ...item })),
      featuredReviews: {
        ...defaultConfig.homepage.featuredReviews,
        ...(incoming.homepage?.featuredReviews ?? {}),
      },
      miniCta: {
        ...defaultConfig.homepage.miniCta,
        ...(incoming.homepage?.miniCta ?? {}),
      },
      bottomCta: {
        ...defaultConfig.homepage.bottomCta,
        ...(incoming.homepage?.bottomCta ?? {}),
      },
    },
    // Build the company block field-by-field so legacy footerBlurb /
    // trustBadges data (from before they moved to `brand`) doesn't
    // leak through the spread and pollute the in-memory config.
    company: {
      aboutHeading: incoming.company?.aboutHeading ?? defaultConfig.company.aboutHeading,
      aboutSubheading: incoming.company?.aboutSubheading ?? defaultConfig.company.aboutSubheading,
      valuesHeading: incoming.company?.valuesHeading ?? defaultConfig.company.valuesHeading,
      aboutBody: incoming.company?.aboutBody?.length
        ? [...incoming.company.aboutBody]
        : [...defaultConfig.company.aboutBody],
      values: incoming.company?.values?.length
        ? incoming.company.values.map((item) => ({ ...item }))
        : defaultConfig.company.values.map((item) => ({ ...item })),
      hours: {
        ...defaultConfig.company.hours,
        ...(incoming.company?.hours ?? {}),
      },
    },
    reviews: {
      ...defaultConfig.reviews,
      ...(incoming.reviews ?? {}),
      items: incoming.reviews?.items?.length
        ? incoming.reviews.items.map((item) => ({ ...item }))
        : defaultConfig.reviews.items.map((item) => ({ ...item })),
    },
    // Gallery items: rewrite legacy category keys (e.g. "decks" →
    // "deck-installation") so photos tagged before the category refactor
    // line up with today's service-slug-based categories. Idempotent.
    gallery: incoming.gallery?.length
      ? incoming.gallery.map((item) => ({
          ...item,
          category: migrateLegacyCategory(item.category),
          // Default for configs stored before the flag existed.
          primary: item.primary ?? false,
          // Drop unsafe/empty thumbnail URLs so the renderer falls back to
          // `url` rather than emitting a broken/empty <img src>.
          thumbnailUrl: safeUrl(item.thumbnailUrl) || undefined,
        }))
      : defaultConfig.gallery.map((item) => ({ ...item })),
    contact: {
      ...defaultConfig.contact,
      ...(incoming.contact ?? {}),
      // Per-row merge so a freshly-added platform from defaults shows
      // up even if the saved config only has a subset of platforms.
      socialLinks: incoming.contact?.socialLinks?.length
        ? incoming.contact.socialLinks.map((link) => ({
            platform: typeof link?.platform === "string" ? link.platform : "",
            url: safeUrl(link?.url),
            label: typeof link?.label === "string" ? link.label : "",
          }))
        : defaultConfig.contact.socialLinks.map((link) => ({ ...link })),
    },
    features: { ...defaultConfig.features, ...(incoming.features ?? {}) },
    serviceAreas: incoming.serviceAreas?.length
      ? incoming.serviceAreas.map((county) => ({
          ...county,
          towns: county.towns?.length
            ? county.towns.map((town) => ({ ...town }))
            : [],
        }))
      : [],
    // Per-slug merge: every default slug is preserved with its baked-in copy,
    // and any matching slug in the incoming config wins on a per-field basis
    // (so e.g. a CMS save of just `subtitle` keeps the default `description`).
    // Incoming slugs that aren't in the defaults pass through untouched in
    // case they were added ahead of a code rollout.
    serviceDetails: (() => {
      const merged: Record<string, ServiceDetail> = {};
      const incomingDetails = incoming.serviceDetails ?? {};
      const allSlugs = new Set([
        ...Object.keys(defaultConfig.serviceDetails),
        ...Object.keys(incomingDetails),
      ]);
      for (const slug of allSlugs) {
        const def = defaultConfig.serviceDetails[slug];
        const inc = incomingDetails[slug];
        merged[slug] = {
          headline:
            typeof inc?.headline === "string" ? inc.headline : (def?.headline ?? ""),
          title: inc?.title ?? def?.title ?? "",
          subtitle: inc?.subtitle ?? def?.subtitle ?? "",
          description: inc?.description ?? def?.description ?? "",
          highlights: inc?.highlights?.length
            ? [...inc.highlights]
            : def?.highlights
              ? [...def.highlights]
              : [],
        };
      }
      return merged;
    })(),
    contactPage: {
      ...defaultConfig.contactPage,
      ...(incoming.contactPage ?? {}),
      trustItems: normalizeTrustBadges(
        incoming.contactPage?.trustItems,
        defaultConfig.contactPage.trustItems,
      ),
    },
    galleryPage: {
      ...defaultConfig.galleryPage,
      ...(incoming.galleryPage ?? {}),
    },
    servicePage: {
      ...defaultConfig.servicePage,
      ...(incoming.servicePage ?? {}),
    },
  };
}
